import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild
} from '@headlessui/react';
import { Mail, Phone, GraduationCap, Calendar, X, Users } from 'lucide-react';
import { Fragment } from 'react';

const StudentsDialog = ({
  isOpen,
  setIsOpen,
  studentsList
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  studentsList: {
    studentId: string;
    studentName?: string;
    emailId?: string;
    mobileNumber?: string;
    branch?: string;
    yearOfPassing?: number;
  }[];
}) => {
  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('+91')) {
      return phone.replace('+91', '+91 ');
    }
    return phone;
  };
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-50'
        onClose={() => setIsOpen(false)}
      >
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
        </TransitionChild>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <TransitionChild
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <DialogPanel className='w-full max-w-2xl rounded-2xl bg-white shadow-2xl'>
                {/* Header */}
                <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
                  <div className='flex items-center gap-3'>
                    <div className='flex h-10 w-10 items-center justify-center rounded-full bg-blue-100'>
                      <Users className='h-5 w-5 text-blue-600' />
                    </div>
                    <div>
                      <DialogTitle className='text-xl font-semibold text-gray-900'>
                        Assigned Students
                      </DialogTitle>
                      <p className='text-sm text-gray-500'>
                        {studentsList?.length || 0}{' '}
                        {studentsList?.length === 1 ? 'student' : 'students'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className='rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
                  >
                    <X className='h-5 w-5' />
                  </button>
                </div>

                {/* Student List */}
                <div className='max-h-96 overflow-y-auto px-6 py-4'>
                  {studentsList?.length === 0 ? (
                    <div className='flex flex-col items-center justify-center py-12'>
                      <div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100'>
                        <Users className='h-8 w-8 text-gray-400' />
                      </div>
                      <p className='text-sm font-medium text-gray-900'>
                        No students assigned
                      </p>
                      <p className='mt-1 text-sm text-gray-500'>
                        Students will appear here once assigned
                      </p>
                    </div>
                  ) : (
                    <div className='space-y-3'>
                      {studentsList?.map((student, idx) => (
                        <div
                          key={student?.studentId ?? idx}
                          className='group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-blue-300 hover:shadow-md'
                        >
                          {/* Student Name */}
                          <div className='mb-3 flex items-center justify-between'>
                            <h3 className='text-lg font-semibold text-gray-900'>
                              {student?.studentName ?? 'Unknown Student'}
                            </h3>
                            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-sm font-medium text-blue-600'>
                              {student?.studentName?.[0]?.toUpperCase() ?? '-'}
                            </div>
                          </div>

                          {/* Student Info Grid */}
                          <div className='space-y-2'>
                            {student?.emailId && (
                              <div className='flex items-center gap-3 text-sm'>
                                <Mail className='h-4 w-4 flex-shrink-0 text-gray-400' />
                                <a
                                  href={`mailto:${student?.emailId}`}
                                  className='text-gray-600 hover:text-blue-600 hover:underline'
                                >
                                  {student?.emailId ?? '-'}
                                </a>
                              </div>
                            )}

                            {student?.mobileNumber && (
                              <div className='flex items-center gap-3 text-sm'>
                                <Phone className='h-4 w-4 flex-shrink-0 text-gray-400' />
                                <a
                                  href={`tel:${student.mobileNumber}`}
                                  className='text-gray-600 hover:text-blue-600 hover:underline'
                                >
                                  {formatPhoneNumber(student?.mobileNumber) ??
                                    '-'}
                                </a>
                              </div>
                            )}

                            {student?.branch && (
                              <div className='flex items-center gap-3 text-sm'>
                                <GraduationCap className='h-4 w-4 flex-shrink-0 text-gray-400' />
                                <span className='text-gray-600'>
                                  {student?.branch ?? '-'}
                                </span>
                              </div>
                            )}

                            {student?.yearOfPassing && (
                              <div className='flex items-center gap-3 text-sm'>
                                <Calendar className='h-4 w-4 flex-shrink-0 text-gray-400' />
                                <span className='text-gray-600'>
                                  Year Of Passing{' '}
                                  {student?.yearOfPassing ?? '-'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className='border-t border-gray-200 px-6 py-4 text-center'>
                  <button
                    onClick={() => setIsOpen(false)}
                    className='rounded-lg bg-blue-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                  >
                    Close
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StudentsDialog;
